import React from 'react';
import { CreditCard, DollarSign, Mail, ShieldCheck, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';

const CURRENT_PRICING = {
  freePlanTokens: '7,000 one-time trial tokens',
  freePlanPrice: '$0',
  explorerPlanTokens: '12,500 tokens / month (150,000 / year)',
  explorerPlanPrice: '$19.99/month or $199/year',
  scholarPlanTokens: '25,000 tokens / month (300,000 / year)',
  scholarPlanPrice: '$39.99/month or $399/year',
  sagePlanTokens: '50,000 tokens / month (600,000 / year)',
  sagePlanPrice: '$79.99/month or $799/year',
  topUpPrice: '$9.99',
} as const;

const PricingPage: React.FC = () => {
  const { t } = useTranslation(['pricing_policy', 'seo']);

  return (
    <div className="min-h-screen bg-white text-gray-800 py-12 px-4">
      <SEO
        title={t('seo:pricing_policy.title')}
        description={t('seo:pricing_policy.description')}
        url="/pricing-policy"
      />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-4xl md:text-5xl font-extrabold text-center mb-8 text-black">
          {t('pricing_policy.page_title')}
        </h1>
        <p className="text-center text-lg mb-12 italic">
          {t('pricing_policy.effective_date')}
        </p>

        {/* Intro Section */}
        <section className="mb-12">
          <p className="text-lg leading-relaxed">
            {t('pricing_policy.intro_text')} <span className="font-bold text-black">{t('pricing_policy.company_name')}</span>. {t('pricing_policy.terms_reference')} <a href="/terms" className="text-black underline">{t('pricing_policy.terms_link')}</a>. {t('pricing_policy.agreement_text')}
          </p>
        </section>

        {/* Sections */}
        <section className="space-y-12">
          {/* 1. Pricing Plans */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <DollarSign size={28} /> {t('pricing_policy.section_1_title')}
            </h2>
            <p className="mb-4">
              {t('pricing_policy.section_1_description')}
            </p>
            <div className="space-y-6">
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="font-semibold">{t('pricing_policy.free_plan_name')}</p>
                <p>{CURRENT_PRICING.freePlanTokens} | <strong>{CURRENT_PRICING.freePlanPrice}</strong></p>
                <p>{t('pricing_policy.free_plan_features')}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="font-semibold">{t('pricing_policy.explorer_plan_name')}</p>
                <p>{CURRENT_PRICING.explorerPlanTokens}</p>
                <p>{CURRENT_PRICING.explorerPlanPrice}</p>
                <p>{t('pricing_policy.explorer_plan_features')}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="font-semibold">{t('pricing_policy.scholar_plan_name')}</p>
                <p>{CURRENT_PRICING.scholarPlanTokens}</p>
                <p>{CURRENT_PRICING.scholarPlanPrice}</p>
                <p>{t('pricing_policy.scholar_plan_features')}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="font-semibold">{t('pricing_policy.sage_plan_name')}</p>
                <p>{CURRENT_PRICING.sagePlanTokens}</p>
                <p>{CURRENT_PRICING.sagePlanPrice}</p>
                <p>{t('pricing_policy.sage_plan_features')}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="font-semibold">{t('pricing_policy.enlightened_plan_name')}</p>
                <p>{t('pricing_policy.enlightened_plan_tokens')} | <strong>{t('pricing_policy.enlightened_plan_price')}</strong></p>
                <p>{t('pricing_policy.enlightened_plan_features')}</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              {t('pricing_policy.india_pricing_note', {
                defaultValue: 'For Indian customers, localized INR pricing is shown in-app at checkout.',
              })}
            </p>
            <p className="mt-4 font-semibold">{t('pricing_policy.annual_discount_title')}</p>
            <p>{t('pricing_policy.annual_discount_description')}</p>
          </div>

          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('pricing_policy.topup_title', { defaultValue: 'Token Top-Ups' })}
            </h2>
            <p>
              {t('pricing_policy.topup_description', {
                defaultValue: `Paid plans can purchase additional token packs for ${CURRENT_PRICING.topUpPrice} (one-time). Top-up tokens are added to your balance but do not carry over past your next billing renewal.`,
              })}
            </p>
          </div>

          {/* 2. Payment and Billing */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <CreditCard size={28} /> {t('pricing_policy.section_2_title')}
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>{t('pricing_policy.payment_methods_label')}</strong> {t('pricing_policy.payment_methods_text')}</li>
              <li><strong>{t('pricing_policy.payment_term_label')}</strong> {t('pricing_policy.payment_term_text')}</li>
              <li><strong>{t('pricing_policy.payment_renewal_label')}</strong> {t('pricing_policy.payment_renewal_text')}</li>
              <li><strong>{t('pricing_policy.failed_payments_label')}</strong> {t('pricing_policy.failed_payments_text')}</li>
              <li><strong>{t('pricing_policy.top_ups_label')}</strong> {t('pricing_policy.top_ups_text')}</li>
              <li><strong>{t('pricing_policy.rollover_label')}</strong> {t('pricing_policy.rollover_text')}</li>
              <li><strong>{t('pricing_policy.exhaustion_label')}</strong> {t('pricing_policy.exhaustion_text')}</li>
            </ul>
          </div>

          {/* App-store pricing explainer — WEBSITE ONLY. Naming web pricing
              INSIDE the app is App Store 3.1.1/3.1.3 + Play anti-steering; here
              on our own site it is allowed and answers the "why is the app more
              expensive?" question. Kept factual/non-disparaging: explains the
              store commission, no "buy here to save" CTA. Inline defaultValue
              matches the india_pricing_note / topup pattern (English fallback in
              every locale; translations can layer in later). */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('pricing_policy.iap_pricing_title', { defaultValue: 'App Store & Google Play Pricing' })}
            </h2>
            <p>
              {t('pricing_policy.iap_pricing_body', {
                defaultValue:
                  "When you subscribe or buy a credit pack inside our iOS or Android app, Apple's App Store or Google Play applies its standard platform commission — which can range from about 15% to 30% — to each transaction, and that cost is reflected in the in-app price. Prices shown on this website are our standard rates. Either way, you receive the same plans, features, and credits.",
              })}
            </p>
          </div>

          {/* 3. Cancellation */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('pricing_policy.section_3_title')}
            </h2>
            <p>
              <strong>{t('pricing_policy.cancellation_you_label')}</strong> {t('pricing_policy.cancellation_you_text')}<br />
              <strong>{t('pricing_policy.cancellation_us_label')}</strong> {t('pricing_policy.cancellation_us_text')}
            </p>
          </div>

          {/* 4. Refunds */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('pricing_policy.section_4_title')}
            </h2>
            <p className="mb-4">
              <strong>{t('pricing_policy.refunds_general_label')}</strong> {t('pricing_policy.refunds_general_text')}
            </p>
            <p className="font-semibold">{t('pricing_policy.refunds_exceptions_title')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>{t('pricing_policy.refunds_tech_issues_label')}</strong> {t('pricing_policy.refunds_tech_issues_text')}</li>
              <li><strong>{t('pricing_policy.refunds_mistakes_label')}</strong> {t('pricing_policy.refunds_mistakes_text')} <a href="mailto:support@sarvalok.com" className="text-black underline">support@sarvalok.com</a> {t('pricing_policy.refunds_mistakes_action')}</li>
              <li><strong>{t('pricing_policy.refunds_failed_payments_label')}</strong> {t('pricing_policy.refunds_failed_payments_text')}</li>
            </ul>
          </div>

          {/* 5. Account Termination */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('pricing_policy.section_5_title')}
            </h2>
            <p>
              {t('pricing_policy.section_5_text')}
            </p>
          </div>

          {/* 6. Free Trial */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('pricing_policy.section_6_title')}
            </h2>
            <p>
              {t('pricing_policy.section_6_text')}
            </p>
          </div>

          {/* 7. Price Changes */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('pricing_policy.section_7_title')}
            </h2>
            <p>
              {t('pricing_policy.section_7_text')}
            </p>
          </div>

          {/* 8. Contact Information */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <Mail size={28} /> {t('pricing_policy.section_8_title')}
            </h2>
            <p className="space-y-2">
              <span className="flex items-center gap-2">
                <Mail size={20} className="text-black" />
                {t('pricing_policy.contact_support_label')} <a href="mailto:support@sarvalok.com" className="text-black underline">support@sarvalok.com</a>
              </span>
              <span className="flex items-center gap-2">
                <Mail size={20} className="text-black" />
                {t('pricing_policy.contact_enterprise_label')} <a href="mailto:Enterprise@sarvalok.com" className="text-black underline">Enterprise@sarvalok.com</a>
              </span>
              <span className="flex items-center gap-2">
                <Mail size={20} className="text-black" />
                {t('pricing_policy.contact_billing_label')} <a href="mailto:billing@sarvalok.com" className="text-black underline">billing@sarvalok.com</a>
              </span>
            </p>
            <p className="mt-2 italic">
              {t('pricing_policy.contact_response_note', {
                defaultValue: 'For fastest response, please email us. Our team typically responds within one business day.',
              })}
            </p>
          </div>

          {/* 9. Educational Institutions */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('pricing_policy.section_9_title')}
            </h2>
            <p>
              {t('pricing_policy.section_9_text')} <a href="mailto:Enterprise@sarvalok.com" className="text-black underline">Enterprise@sarvalok.com</a> {t('pricing_policy.section_9_action')}
            </p>
          </div>

          {/* 10. Data Security */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <ShieldCheck size={28} /> {t('pricing_policy.section_10_title')}
            </h2>
            <p>
              {t('pricing_policy.section_10_text')}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PricingPage;
